import React from 'react';

import { graphql, useFragment } from 'react-relay';

import Avatar, { type AvatarPropTypes, type SizeType } from '@atlaskit/avatar';

import type { avatarRelay_UsePictureAndAppTypeFragment$key } from './__generated__/avatarRelay_UsePictureAndAppTypeFragment.graphql';
import { getAppearanceForAppType } from './utils';

export type AvatarRelayProps = Omit<AvatarPropTypes, 'src' | 'appearance'> & {
	size?: SizeType;
	fragmentRef: avatarRelay_UsePictureAndAppTypeFragment$key | null;
};

/**
 * __Avatar relay__
 *
 * Displays a user's profile image with an appearance derived from the user's appType:
 * - circle for users and system
 * - square for teams
 * - hexagon for agents
 *
 * Forwards Avatar props such as label, size, presence, and status.
 */
export const AvatarRelay = ({
	fragmentRef,
	label,
	borderColor,
	children,
	href,
	isDisabled,
	name,
	presence,
	size,
	status,
	stackIndex,
	tabIndex,
	target,
	onClick,
	testId,
	analyticsContext,
	as,
	isDecorative,
	imgLoading,
}: AvatarRelayProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment avatarRelay_UsePictureAndAppTypeFragment on User {
				picture
				... on AppUser {
					appType
				}
			}
		`,
		fragmentRef,
	);
	const appearance = getAppearanceForAppType(data?.appType);

	return (
		<Avatar
			// from relay fragment
			src={data?.picture}
			appearance={appearance}
			// prop-drill from AvatarRelayProps
			label={label}
			borderColor={borderColor}
			children={children}
			href={href}
			isDisabled={isDisabled}
			name={name}
			presence={presence}
			size={size}
			status={status}
			stackIndex={stackIndex}
			tabIndex={tabIndex}
			target={target}
			onClick={onClick}
			testId={testId}
			analyticsContext={analyticsContext}
			as={as}
			isDecorative={isDecorative}
			imgLoading={imgLoading}
		/>
	);
};
